import { _ } from "../../../../../core/polyglot/polyglot.js";

/*
 *
 *        /--> CHANGE TYPE \
 *       /                  \
 * VIEW  ------------------------> EDIT ------> VIEW
 *   |    \                  /             /
 *   |     \-->  OVERRIDE   /             /
 *	 |                                   /
 *	 |                                  /
 *	  \------> EDIT MANUAL ENTRY ------
 *
 */

/**
 * Abstract RuleTemplatesWidget state. This is a stateless state
 * of a quite minimalistic state machine.
 *
 * @abstract
 */
class AbstractRuleTemplateState {
	constructor() {
		// This is needed to avoid a reference cycle:
		Object.defineProperty(this, "_ruleTemplatesWidgets", {
			enumerable: false,
			value: [],
			writable: true,
		});
	}

	addWidget(widget) {
		this._ruleTemplatesWidgets.push(widget);
	}

	getTitle() {
		throw new Error("Subclass responsibility");
	}

	goToViewState() {}

	goToEditState() {}

	goToChangeTypeState() {}

	goToOverrideState() {}

	goToEditManualEntryState() {}

	predictionRuleTitleRenderer(_widget) {
		return () => {};
	}

	buttonsRenderer(widget) {
		return (html) => widget.renderNonEditButtonsOn(html);
	}

	ownRuleButtonsRenderer(widget) {
		return widget.makeOwnButtons();
	}

	inheritedRuleButtonsRenderer(widget) {
		return (html) => widget.renderEditInheritedRuleButtonsOn(html);
	}

	predictionRuleTemplateRenderer(widget) {
		return (html) => widget.renderEditPredictionRuleTemplateOn(html);
	}

	balanceRuleTemplateRenderer(widget) {
		return (html) => widget.renderEditBalanceRuleTemplateOn(html);
	}

	descriptionRenderer() {
		return () => {};
	}

	ownRuleTemplateRenderer(widget) {
		return widget.makeRuleTemplateViewer();
	}

	inheritedRuleTemplateRenderer(widget) {
		return widget.makeInheritedRuleTemplateViewer();
	}

	ruleTemplateDescriptionRenderer(widget) {
		return widget.makeDescriptionViewer();
	}

	createRuleTemplateRenderer(_widget) {
		return () => {};
	}

	getManualEntriesTitle() {
		return _("One off values");
	}

	manualEntryButtonsRenderer() {
		return () => {};
	}

	manualEntryRuleTitleRenderer() {
		return () => {};
	}

	manualEntryRuleTemplateRenderer() {
		return () => {};
	}

	manualEntryRuleTemplateCollectionRenderer() {
		return () => {};
	}

	isReadOnly() {
		return false;
	}

	_transitionToState(StateClass) {
		const state = new StateClass();

		this._ruleTemplatesWidgets.forEach((widget) => {
			state.addWidget(widget);

			widget.changeState({
				state,
			});
		});
	}
}

/**
 * Read-only state for the RuleTemplatesWidget.
 * In this state, no modifications can be performed, thus no buttons are
 * rendered.
 */
export class ReadOnlyState extends AbstractRuleTemplateState {
	buttonsRenderer() {
		return () => {};
	}

	manualEntryRuleTitleRenderer(widget) {
		return (html) => widget.renderEditingManualEntryRuleTitleOn(html);
	}

	manualEntryRuleTemplateCollectionRenderer(widget) {
		return (html) => widget.renderManualEntryRuleTemplateCollectionOn(html);
	}

	isReadOnly() {
		return true;
	}
}

/**
 * Default state of the RuleTemplatesWidget.
 * In view mode, the user can visualize the current rule(s) of a cell
 * and edit/change/override them.
 */
export class ViewState extends AbstractRuleTemplateState {
	getTitle() {
		return _("Add rule");
	}

	goToReadOnlyState() {
		this._transitionToState(ReadOnlyState);
	}

	goToEditState() {
		// eslint-disable-next-line no-use-before-define -- Circular linking
		this._transitionToState(EditState);
	}

	goToChangeTypeState() {
		// eslint-disable-next-line no-use-before-define -- Circular linking
		this._transitionToState(ChangeTypeState);
	}

	goToOverrideState() {
		// eslint-disable-next-line no-use-before-define -- Circular linking
		this._transitionToState(OverrideState);
	}

	goToEditManualEntryState() {
		// eslint-disable-next-line no-use-before-define -- Circular linking
		this._transitionToState(EditManualEntryState);
	}

	descriptionRenderer(widget) {
		return (html) => widget.renderDescriptionOn(html);
	}

	manualEntryRuleTemplateCollectionRenderer(widget) {
		return (html) => widget.renderManualEntryRuleTemplateCollectionOn(html);
	}

	manualEntryButtonsRenderer(widget) {
		return (html) => widget.renderCreateButtonsOn(html);
	}
}

/**
 * This state is used to edit the values of an existing own rule.
 */
export class EditState extends AbstractRuleTemplateState {
	goToViewState() {
		this._transitionToState(ViewState);
	}

	predictionRuleTitleRenderer(widget) {
		return (html) => widget.renderEditingPredictionRuleTitleOn(html);
	}

	buttonsRenderer(widget) {
		return (html) => widget.renderEditButtonsOn(html);
	}

	ownRuleTemplateRenderer(widget) {
		return widget.getEditor();
	}

	inheritedRuleTemplateRenderer(widget) {
		return widget.getEditor();
	}

	createRuleTemplateRenderer(widget) {
		return widget.getEditor();
	}
}

/**
 * Abstract state used to change or override a rule.
 */
class AbstractChangeState extends AbstractRuleTemplateState {
	goToViewState() {
		this._transitionToState(ViewState);
	}

	goToEditState() {
		this._transitionToState(EditState);
	}

	ownRuleButtonsRenderer(widget) {
		return (html) => widget.renderCreateButtonsWithCancelOn(html);
	}

	inheritedRuleButtonsRenderer(widget) {
		return (html) => widget.renderCreateButtonsWithCancelOn(html);
	}

	predictionRuleTemplateRenderer() {
		return () => {};
	}

	balanceRuleTemplateRenderer() {
		return () => {};
	}
}

/**
 * This state is used to change the type of existing own rule.
 */
export class ChangeTypeState extends AbstractChangeState {
	getTitle() {
		return _("Change rule");
	}
}

/**
 * This state is used to override an existing inherited rule.
 */
class OverrideState extends AbstractChangeState {
	getTitle() {
		return _("Override rule");
	}
}

/**
 * This state is used to edit the values of a manual entry rule.
 */
class EditManualEntryState extends AbstractRuleTemplateState {
	goToViewState() {
		this._transitionToState(ViewState);
	}

	manualEntryRuleTitleRenderer(widget) {
		return (html) => widget.renderEditingManualEntryRuleTitleOn(html);
	}

	manualEntryRuleTemplateRenderer(widget) {
		return (html) => widget.renderEditManualEntryRuleTemplateOn(html);
	}

	ownRuleTemplateRenderer(widget) {
		return widget.getEditor();
	}

	predictionRuleTemplateRenderer() {
		return () => {};
	}

	balanceRuleTemplateRenderer() {
		return () => {};
	}

	buttonsRenderer() {
		return () => {};
	}

	manualEntryButtonsRenderer(widget) {
		return (html) => widget.renderEditButtonsOn(html);
	}

	createRuleTemplateRenderer(widget) {
		return widget.getEditor();
	}
}
